/**
 * API client (BUILD_SPEC.md §6.1, §7.5).
 *
 * Axios instance configured for the SPA. Two interceptors implement
 * the auth flow:
 *
 *   • Request: attach `Authorization: Bearer <accessToken>` from the
 *     in-memory auth store on every request. Pulled imperatively so
 *     React's render cycle is not on the hot path.
 *
 *   • Response: on a 401, attempt a single refresh against
 *     /api/auth/refresh. If that succeeds, retry the original request
 *     once with the new token. Concurrent 401s share the same in-flight
 *     refresh promise so we never fire more than one refresh per
 *     window — preventing the rotation from racing itself (§7.3 makes
 *     each refresh single-use; double-firing would invalidate the new
 *     token before the second caller sees it).
 *
 * `withCredentials: true` is essential — without it the browser would
 * not include the refresh cookie on the /api/auth/refresh call.
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { authStore } from "@/stores/authStore";

/** Internal flag we tag onto the axios config to mark a retry attempt
 *  so the interceptor doesn't loop on a refresh that itself failed. */
interface RetryableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

export const api: AxiosInstance = axios.create({
  // Same-origin in dev (Vite proxies /api → :3000) and in prod (Nginx).
  // Keeping baseURL relative makes deployment routing trivial.
  baseURL: "/api",
  withCredentials: true,
  // 15s ceiling — enough for Argon2id verify on a slow box; short
  // enough that a hung backend never freezes the UI indefinitely.
  timeout: 15_000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = authStore.getToken();
  if (token && config.headers) {
    config.headers.set?.("Authorization", `Bearer ${token}`);
  }
  return config;
});

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Calls /api/auth/refresh and returns the fresh access token (or null
 * if refresh failed). Concurrent callers receive the same promise so
 * we never rotate twice for a single 15-min window.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const res = await axios.post<{ accessToken: string }>(
        "/api/auth/refresh",
        {},
        { withCredentials: true, timeout: 15_000 },
      );
      authStore.setToken(res.data.accessToken);
      return res.data.accessToken;
    } catch {
      authStore.clear();
      return null;
    } finally {
      // Release the lock after the promise settles either way.
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;
    if (!original || original._retried || error.response?.status !== 401) {
      return Promise.reject(error);
    }
    // Don't try to refresh if the failing call WAS the refresh.
    if (typeof original.url === "string" && original.url.includes("/auth/refresh")) {
      authStore.clear();
      return Promise.reject(error);
    }
    original._retried = true;
    const newToken = await refreshAccessToken();
    if (!newToken) return Promise.reject(error);
    original.headers = original.headers ?? {};
    (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
    return api.request(original);
  },
);

/**
 * Imperative refresh helper for the proactive 10-minute timer (§7.5)
 * and window-focus handlers. Returns true if the session is still
 * valid after the call.
 */
export async function proactiveRefresh(): Promise<boolean> {
  const token = await refreshAccessToken();
  return token !== null;
}
